import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, FolderKanban, Building2,
  MapPin, Calendar, Trash2,
  Clock, ChevronRight, GanttChart as GanttChartIcon,
  Columns, GripVertical as DragHandle, Paperclip,
  Calculator, ShoppingCart, ShieldCheck, FileCheck,
  Presentation, FileText, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ExportDropdown } from '../components/shared/PageActions';
import PanelField from '../components/shared/PanelField';
import DetailTab from '../components/shared/DetailTab';
import InfoCard from '../components/shared/InfoCard';
import CollapsiblePanel from '../components/shared/CollapsiblePanel';
import { getDefaultCompanyInfo } from '../lib/brandConfig';
import { previewProjectPdf, downloadProjectPdf } from '../lib/infoCardPdfExport';
import ProjectScheduleTab from '../components/projects/ProjectScheduleTab';
import ProjectCalendarTab from '../components/projects/ProjectCalendarTab';
import FilesTab from '../components/projects/FilesTab';
import CostEstimateTab from '../components/projects/CostEstimateTab';
import PurchaseOrdersTab from '../components/projects/PurchaseOrdersTab';
import SWMSTab from '../components/projects/SWMSTab';
import OCSubmissionTab from '../components/projects/OCSubmissionTab';
import ProjectQuotesTab from '../components/projects/ProjectQuotesTab';
import ProjectInvoicesTab from '../components/projects/ProjectInvoicesTab';
import ProjectProposalsTab from '../components/projects/ProjectProposalsTab';
import ProjectStatementTab from '../components/projects/ProjectStatementTab';
import { format } from 'date-fns';
import axios from 'axios';

import { apiErrorMessage } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status badge ─────────────────────────────────────────────────────────────
function ProjectStatusBadge({ status }) {
  const styles = {
    planning:   { bg: '#dbeafe', color: '#1e40af', border: '#3b82f6' },
    active:     { bg: '#d1fae5', color: '#065f46', border: '#10b981' },
    completed:  { bg: '#f3f4f6', color: '#6b7280', border: '#9ca3af' },
    on_hold:    { bg: '#fef3c7', color: '#92400e', border: '#f59e0b' },
    cancelled:  { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' },
  };
  const s = styles[status] || styles.planning;
  const label = status === 'on_hold' ? 'On Hold' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}33` }}
    >
      {label}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Determine initial tab from route: /projects/:id/schedule → 'schedule', /projects/:id/calendar → 'calendar'
  const isScheduleRoute = location.pathname.endsWith('/schedule');
  const isCalendarRoute = location.pathname.endsWith('/calendar');
  const [activeRightTab, setActiveRightTab] = useState(
    isCalendarRoute ? 'calendar' : isScheduleRoute ? 'schedule' : 'overview'
  );
  const [panelCollapsed, setPanelCollapsed] = useState(isScheduleRoute || isCalendarRoute);

  // Cross-tab sync state
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const scrollToDayRef = useRef(null);

  // Auto-clear highlight after 3s
  useEffect(() => {
    if (!highlightedTaskId) return;
    const timer = setTimeout(() => setHighlightedTaskId(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightedTaskId]);

  // The Schedule grid needs the horizontal room: with the 320px side panel open
  // the date columns are pushed off-screen. Collapse it on arrival (the user can
  // still reopen it), and leave other tabs alone.
  useEffect(() => {
    if (activeRightTab === 'schedule') setPanelCollapsed(true);
  }, [activeRightTab]);

  // ── Fetch project ─────────────────────────────────────────────────────────
  const fetchProject = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${id}`);
      setProject(res.data);
    } catch (err) {
      console.error('Failed to fetch project:', err);
      if (err.response?.status === 404) navigate('/');
      else toast.error('Could not load this project. Check your connection and refresh.');
    }
  }, [id, navigate]);

  // ── Fetch clients for dropdown ────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/clients`).then(res => setClients(res.data?.data || res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchProject().finally(() => setLoading(false));
  }, [fetchProject]);

  // ── Local field change (left panel) ──────────────────────────────────────
  const handleFieldChange = (field, value) => {
    setProject(prev => ({ ...prev, [field]: value }));
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { id: _id, project_no, created_at, client_name, ...updateData } = project;
      const res = await axios.put(`${API}/projects/${id}`, updateData);
      setProject(res.data);
      toast.success('Project saved');
    } catch (err) {
      console.error('Failed to save project:', err);
      toast.error(apiErrorMessage(err, 'Could not save this project.'));
    }
    setSaving(false);
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete project:', err);
      toast.error(apiErrorMessage(err, 'Could not delete this project.'));
    }
  };

  // ── PDF ──────────────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();
  // Both are async — without await + catch a failure is an unhandled rejection
  // and the export button appears to do nothing.
  const runProjectPdf = async (fn, label) => {
    if (!project) return;
    try {
      await fn(project, companyInfo);
      if (label === 'download') toast.success('PDF generated');
    } catch (err) {
      console.error('Project PDF generation failed:', err);
      toast.error('Failed to generate the project PDF.');
    }
  };
  const handlePdfPreview = () => runProjectPdf(previewProjectPdf, 'preview');
  const handlePdfDownload = () => runProjectPdf(downloadProjectPdf, 'download');
  const handlePrint = () => window.print();

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#e5e4df' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  if (!project) return null;

  const taskCount = project.tasks?.length || 0;

  return (
    <div className="min-h-screen" style={{ background: '#e5e4df' }}>
      {/* Covers this page and every tab rendered inside it */}
      <Toaster position="top-right" />

      {/* Top bar */}
      <div className="bg-[#1a1a2e] py-3">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {project.project_name}
              </h1>
              <span className="text-white/50 text-xs font-mono">{project.project_no}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete Project</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {project.project_name}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Body: left panel + right content */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-6">
        <div className="flex gap-0 rounded-lg overflow-hidden shadow-xl" style={{ background: '#f8f7f4' }}>

          {/* ── Left Panel (Navy 320px) ─────────────────────────────────── */}
          <CollapsiblePanel collapsed={panelCollapsed} onToggle={() => setPanelCollapsed(!panelCollapsed)}>
                {/* Section: Project */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Project
                  </h3>
                  <PanelField label="Project Name" value={project.project_name} onChange={v => handleFieldChange('project_name', v)} />
                  <PanelField
                    label="Client"
                    value={project.client_id}
                    onChange={v => handleFieldChange('client_id', v)}
                    type="select"
                    options={[
                      { value: '', label: '— No Client —' },
                      ...clients.map(c => ({ value: c.id, label: c.company_name })),
                    ]}
                  />
                  <PanelField
                    label="Status"
                    value={project.status}
                    onChange={v => handleFieldChange('status', v)}
                    type="select"
                    options={[
                      { value: 'planning', label: 'Planning' },
                      { value: 'active', label: 'Active' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'on_hold', label: 'On Hold' },
                      { value: 'cancelled', label: 'Cancelled' },
                    ]}
                  />
                </div>

                {/* Section: Location & Timeline */}
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Location & Timeline
                  </h3>
                  <PanelField label="Site Address" value={project.site_address} onChange={v => handleFieldChange('site_address', v)} />
                  <PanelField label="Start Date" value={project.start_date} onChange={v => handleFieldChange('start_date', v)} type="date" />
                  <PanelField label="End Date" value={project.end_date} onChange={v => handleFieldChange('end_date', v)} type="date" />
                </div>

                {/* Section: Details */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>
                    Details
                  </h3>
                  <PanelField label="Description" value={project.description} onChange={v => handleFieldChange('description', v)} type="textarea" />
                </div>
          </CollapsiblePanel>

          {/* ── Right Content ────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Right tabs */}
            {/* Row 1 — site execution */}
            <div
              className="flex border-b px-4"
              style={{ borderColor: 'rgba(201,168,76,0.15)' }}
            >
              {[
                { key: 'overview', label: 'Overview' },
                // Tasks tab retired: the Schedule tab edits every field it did,
                // plus real dates, and saves per task instead of rewriting the
                // whole project document.
                { key: 'schedule', label: `Schedule (${taskCount})`, icon: GanttChartIcon },
                { key: 'calendar', label: 'Calendar',  icon: Calendar },
                { key: 'files',    label: 'Files',     icon: Paperclip },
              ].map(t => (
                <DetailTab key={t.key} active={activeRightTab === t.key} onClick={() => setActiveRightTab(t.key)} label={t.label} />
              ))}
            </div>

            {/* Accent divider between rows */}
            <div style={{ height: '1px', background: '#c9a84c', opacity: 0.4 }} />

            {/* Row 2 — commercial & compliance */}
            <div
              className="flex border-b px-4"
              style={{ borderColor: 'rgba(201,168,76,0.15)' }}
            >
              {[
                { key: 'proposals',       label: 'Proposals',       icon: Presentation },
                { key: 'quotes',          label: 'Quotes',          icon: FileText },
                { key: 'invoices',        label: 'Invoices',        icon: Receipt },
                { key: 'statement',       label: 'Statement',       icon: Receipt },
                { key: 'cost-estimate',   label: 'Cost Estimate',   icon: Calculator },
                { key: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
                { key: 'swms',            label: 'SWMS',            icon: ShieldCheck },
                { key: 'oc-submission',   label: 'OC Submission',   icon: FileCheck },
              ].map(t => (
                <DetailTab key={t.key} active={activeRightTab === t.key} onClick={() => setActiveRightTab(t.key)} label={t.label} />
              ))}
            </div>

            {/* Tab content */}
            <div className="p-6">
              {activeRightTab === 'overview' && (
                <OverviewTab project={project} clients={clients} />
              )}
              {activeRightTab === 'schedule' && (
                <ScheduleTab project={project} />
              )}
              {activeRightTab === 'calendar' && (
                <CalendarTab project={project} />
              )}
              {activeRightTab === 'files' && (
                <FilesTab project={project} />
              )}
              {activeRightTab === 'proposals' && (
                <ProjectProposalsTab project={project} />
              )}
              {activeRightTab === 'quotes' && (
                <ProjectQuotesTab project={project} />
              )}
              {activeRightTab === 'invoices' && (
                <ProjectInvoicesTab project={project} />
              )}
              {activeRightTab === 'statement' && (
                <ProjectStatementTab project={project} />
              )}
              {activeRightTab === 'cost-estimate' && (
                <CostEstimateTab project={project} />
              )}
              {activeRightTab === 'purchase-orders' && (
                <PurchaseOrdersTab project={project} />
              )}
              {activeRightTab === 'swms' && (
                <SWMSTab project={project} onProjectChange={setProject} />
              )}
              {activeRightTab === 'oc-submission' && (
                <OCSubmissionTab project={project} />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────
function OverviewTab({ project, clients }) {
  const navigate = useNavigate();
  const clientName = project.client_name || clients.find(c => c.id === project.client_id)?.company_name || '—';

  // Calculate duration
  let durationText = '—';
  if (project.start_date && project.end_date) {
    const start = new Date(project.start_date);
    const end = new Date(project.end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (days > 0) {
      durationText = days > 30 ? `${Math.round(days / 30)} months (${days} days)` : `${days} days`;
    }
  }

  // Progress based on tasks
  const tasks = project.tasks || [];
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const progressPct = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InfoCard icon={FolderKanban} label="Project" value={project.project_name} sub={project.project_no} />
        <InfoCard icon={Building2} label="Client" value={
          project.client_id ? (
            <span
              className="text-blue-600 hover:underline cursor-pointer"
              onClick={() => navigate(`/clients/${project.client_id}`)}
            >
              {clientName}
            </span>
          ) : '—'
        } />
        <InfoCard icon={FolderKanban} label="Status" value={<ProjectStatusBadge status={project.status} />} />
        <InfoCard icon={MapPin} label="Site Address" value={project.site_address || '—'} />
        <InfoCard icon={Calendar} label="Start Date" value={project.start_date || '—'} />
        <InfoCard icon={Calendar} label="End Date" value={project.end_date || '—'} />
        <InfoCard icon={Clock} label="Duration" value={durationText} />
        <InfoCard icon={FolderKanban} label="Tasks" value={`${tasks.length} total (${completedTasks} completed)`} />
      </div>

      {/* Progress bar */}
      {tasks.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#1a1a2e] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Progress
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: '#e5e4df' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: progressPct === 100 ? '#10b981' : '#c9a84c',
                }}
              />
            </div>
            <span className="text-sm font-bold font-mono text-[#1a1a2e]">{progressPct}%</span>
          </div>
        </div>
      )}

      {/* Description */}
      {project.description && (
        <div>
          <h3 className="text-sm font-semibold text-[#1a1a2e] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Description</h3>
          <div className="p-4 rounded-md text-sm text-[#2c2c2c] whitespace-pre-wrap" style={{ background: '#f3f2ef', border: '1px solid #e5e4df' }}>
            {project.description}
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="flex gap-4 text-xs text-[#9ca3af]">
        <span>Created: {project.created_at ? new Date(project.created_at).toLocaleDateString() : '—'}</span>
        <span>Updated: {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : '—'}</span>
      </div>
    </div>
  );
}

// ── Schedule tab (editable grid + Gantt) ──────────────────────────────────
// Was a read-only GanttChart with no way to change a date. ProjectScheduleTab
// owns its own data via useProjectSchedule and saves per-task, so it no longer
// depends on the project-level Save button.
function ScheduleTab({ project }) {
  return <ProjectScheduleTab project={project} />;
}

// ── Calendar tab ──────────────────────────────────────────────────────────
// Was the THIRD copy of the timeline-anchor derivation. Now reads the same
// absolute dates as the Schedule tab through useProjectSchedule, so the two
// views cannot disagree — there is nothing left to derive.
function CalendarTab({ project }) {
  return <ProjectCalendarTab project={project} />;
}
